/**
 * Pure billing/usage math. The product is a **period-based subscription**: each
 * cycle grants a data ALLOWANCE (bytes, shown as GB); usage is COMPUTED against
 * the backend's monotonic per-org group counter and a per-cycle baseline (see
 * the Model B block below), not debited from a running balance. Money still buys
 * bytes at purchase time (top-ups) and at plan grant. 1 GB = 1e9 bytes (decimal,
 * matching how proxy bandwidth is sold); money is integer micro-USD.
 *
 * Gateway-agnostic by construction: nothing here knows Stripe (or any other
 * rail) exists. It is the shared vocabulary every gateway adapter and the
 * neutral core both speak. Its only import is a TYPE (erased at build time), so
 * it stays pure and isomorphic and unit-tests without a DB (see billing.test.ts).
 */

import type {
  billingMode as billingModeEnum,
  paymentRail as paymentRailEnum,
} from '@/db/schema'
import { isGatewayId } from '@/lib/billing/gateways/types'

const MICRO_USD_PER_USD = 1_000_000
export const BYTES_PER_GB = 1_000_000_000

/**
 * Legacy launch price for one GB, in micro-USD. The AUTHORITATIVE pricing
 * lives in the `pricing_plans` table (effective-dated plan catalog — see
 * lib/billing/pricing.server.ts); this constant survives only as the default
 * parameter of `priceForGb` and in old ledger rows' stamped rates.
 */
export const PRICE_PER_GB_MICRO_USD = 3_000_000

/**
 * One monthly plan of the catalog: `includedGb` of data for
 * `priceMonthlyMicroUsd`. Prepaid top-ups beyond the included data price at
 * the plan's effective per-GB rate (`planPerGbMicroUsd`). The active catalog
 * is read from the DB on the server; this module holds only the pure math so
 * it stays isomorphic and unit-testable.
 */
export interface PricingPlan {
  slug: string
  name: string
  includedGb: number
  priceMonthlyMicroUsd: number
  /** Max GB a single "purchase additional data" top-up may add on this plan. */
  topUpCapGb: number
  highlighted: boolean
}

/** Static fallback catalog (the launch lineup), used when the DB has no rows. */
export const DEFAULT_PRICING_PLANS: PricingPlan[] = [
  {
    slug: 'starter',
    name: 'Starter',
    includedGb: 5,
    priceMonthlyMicroUsd: 30_000_000,
    topUpCapGb: 100,
    highlighted: false,
  },
  {
    slug: 'basic',
    name: 'Basic',
    includedGb: 20,
    priceMonthlyMicroUsd: 100_000_000,
    topUpCapGb: 100,
    highlighted: true,
  },
  {
    slug: 'advanced',
    name: 'Advanced',
    includedGb: 125,
    priceMonthlyMicroUsd: 500_000_000,
    topUpCapGb: 2000,
    highlighted: false,
  },
  {
    slug: 'scale',
    name: 'Scale',
    includedGb: 400,
    priceMonthlyMicroUsd: 1_000_000_000,
    topUpCapGb: 2000,
    highlighted: false,
  },
]

/** A plan's effective per-GB rate: monthly price over included data. */
export function planPerGbMicroUsd(
  plan: Pick<PricingPlan, 'includedGb' | 'priceMonthlyMicroUsd'>,
): number {
  if (plan.includedGb <= 0) throw new Error('includedGb must be positive')
  return Math.round(plan.priceMonthlyMicroUsd / plan.includedGb)
}

/**
 * The per-GB rate for prepaid top-ups sold outside a plan cycle: the BASE
 * (smallest) plan's rate. Once orgs carry a plan, top-ups price at the org's
 * own plan rate instead — this is the unattributed default.
 */
export function topUpRateMicroUsd(plans: PricingPlan[]): number {
  if (plans.length === 0) throw new Error('plan catalog is empty')
  return planPerGbMicroUsd(
    plans.reduce((min, p) => (p.includedGb < min.includedGb ? p : min)),
  )
}

/** Cost in micro-USD to buy `gb` gigabytes at a given per-GB rate. */
export function priceForGb(
  gb: number,
  pricePerGbMicroUsd: number = PRICE_PER_GB_MICRO_USD,
): number {
  if (!Number.isFinite(gb) || gb <= 0) throw new Error('gb must be positive')
  if (pricePerGbMicroUsd <= 0) throw new Error('price per GB must be positive')
  return Math.round(gb * pricePerGbMicroUsd)
}

/**
 * Convert micro-USD to whole cents (the smallest unit gateways charge in).
 * Throws if the amount isn't a whole number of cents — data purchase amounts
 * always are, so a fraction signals a pricing bug rather than something to
 * silently round.
 */
export function microUsdToCents(micros: number): number {
  const cents = micros / 10_000
  if (!Number.isInteger(cents)) {
    throw new Error(`amount ${micros} micro-USD is not a whole number of cents`)
  }
  return cents
}

/**
 * Data capacity (bytes) a payment buys, at a given price per GB. Applied once at
 * data purchase — the running balance never stores money. Floors to whole bytes.
 */
export function bytesForPayment(
  paidMicroUsd: number,
  pricePerGbMicroUsd: number,
): number {
  if (paidMicroUsd < 0) throw new Error('paid amount must be non-negative')
  if (pricePerGbMicroUsd <= 0) throw new Error('price per GB must be positive')
  return Math.floor((paidMicroUsd / pricePerGbMicroUsd) * BYTES_PER_GB)
}

/**
 * Period-based usage math (Model B). The org holds a monthly data ALLOWANCE,
 * not a debited balance. Usage is computed from the backend's monotonic per-org
 * group counter against a per-cycle baseline snapshot; enforcement raises the
 * backend group `bandwidth_limit` to the ceiling rather than resetting the
 * counter. Pure so the server writers, metering, and the dashboard share one
 * definition. Some inputs are signed (a refund can land before its credit).
 */

/** How the cycle-start baseline was captured (persisted on organization_billing).
 *  A non-'live' source is provisional and re-anchored by the first good metering read. */
export type BaselineSource = 'live' | 'cached' | 'fallback'

/** Bytes consumed this cycle: how far the group counter advanced past the
 *  cycle-start baseline. Clamped at 0 so a lagging/unobserved counter never
 *  reports negative usage. */
export function usedThisCycleBytes(
  groupUsed: number,
  groupBaseline: number,
): number {
  return Math.max(0, groupUsed - groupBaseline)
}

/**
 * The org's two capacity buckets, attributed against this cycle's usage.
 *
 * - `cycleAllowanceBytes` EXPIRES at each cycle boundary (plan grants, purchased
 *   data, operator adjustments all land here).
 * - `creditBytes` SURVIVES cycle boundaries (negotiated / prepaid credit).
 *
 * Spend order is ATTRIBUTION, not actuation: usage is a single monotonic group
 * counter, never a debit, so ordering is two `min` calls recomputed from scratch
 * on every read. That makes it stateless and impossible to drift. Cycle
 * allowance fills FIRST so credit — the scarce non-expiring asset — is preserved.
 *
 * The backend gate only ever sees the SUM (see `desiredGroupCeiling`), so no
 * enforcement behaviour follows from the ordering; it matters only at cycle
 * close (what expires) and in the UI (what we show).
 */
export interface CycleSplit {
  /** Bytes consumed since the cycle baseline. */
  usedThisCycle: number
  /** Portion of `usedThisCycle` drawn from the expiring cycle allowance. */
  cycleUsed: number
  /** Portion of `usedThisCycle` drawn from non-expiring credit. */
  creditUsed: number
  /** Expiring allowance left. This is what `planExpiry` removes at cycle close. */
  cycleRemaining: number
  /** Non-expiring credit left. Survives the boundary; settled at each re-frame. */
  creditRemaining: number
  /** Total usable now — the number that answers "can I keep working?". */
  remaining: number
  /** Used beyond BOTH buckets: written off, never billed. */
  overage: number
}

export function splitCycleUsage(
  allowance: number,
  credits: number,
  groupUsed: number,
  groupBaseline: number,
): CycleSplit {
  const usedThisCycle = usedThisCycleBytes(groupUsed, groupBaseline)
  // A refund can drive the allowance negative. That deficit is money owed, not
  // headroom, so it is clamped out of the cycle bucket — but it DOES reduce the
  // credit headroom usable this cycle, otherwise the ceiling we push and the
  // split we display would disagree (the dashboard would offer credit the
  // backend gate refuses). It never reduces the STORED credit balance: the debt
  // evaporates at the next grant's absolute allowance reset.
  const debt = Math.max(0, -allowance)
  const cycleCap = Math.max(0, allowance)
  const creditCap = Math.max(0, Math.max(0, credits) - debt)

  const cycleUsed = Math.min(usedThisCycle, cycleCap)
  const creditUsed = Math.min(creditCap, usedThisCycle - cycleUsed)
  const cycleRemaining = cycleCap - cycleUsed
  const creditRemaining = creditCap - creditUsed

  return {
    usedThisCycle,
    cycleUsed,
    creditUsed,
    cycleRemaining,
    creditRemaining,
    remaining: cycleRemaining + creditRemaining,
    overage: Math.max(0, usedThisCycle - cycleCap - creditCap),
  }
}

/**
 * Remaining EXPIRING allowance this cycle — what a cycle close removes. Use this
 * ONLY at cycle-close sites (`applyPlanCycleGrant`, `expirePlanBucket`); every
 * other caller wants `totalRemainingBytes`.
 */
export function cycleRemainingBytes(
  allowance: number,
  groupUsed: number,
  groupBaseline: number,
): number {
  return Math.max(0, allowance - usedThisCycleBytes(groupUsed, groupBaseline))
}

/** Total usable capacity: expiring allowance plus non-expiring credit. */
export function totalRemainingBytes(
  allowance: number,
  credits: number,
  groupUsed: number,
  groupBaseline: number,
): number {
  return splitCycleUsage(allowance, credits, groupUsed, groupBaseline).remaining
}

/**
 * The billing-row fields the backend ceiling depends on. Taking the ROW rather
 * than loose scalars is deliberate: a caller cannot forget the credit term or
 * the entitlement gate, because it never passes them.
 */
export interface CycleState {
  cycleAllowanceBytes: number
  creditBytes: number
  groupBaselineBytes: number
  subscriptionRef: string | null
  planPeriodEnd: Date | null
}

/**
 * The absolute backend group `bandwidth_limit`: the counter value at which the
 * org's capacity is exhausted. THE single clamp — every group-limit writer goes
 * through `actuateGroupCeiling`, which calls this.
 *
 * Clamped to >= 1 because the backend treats `limit == 0` as UNLIMITED, the one
 * value we must never push. The backend blocks on `group_used >= group_limit`
 * (note `>=`), so a ceiling equal to the live counter blocks — which is what
 * makes the expiry freeze work.
 *
 * Credit is included ONLY while entitled. `expirePlanBucket` freezes the
 * allowance to `min(allowance, used)` so a lapsed org's ceiling collapses to the
 * counter, but credit is deliberately NOT frozen (it has to survive). Without
 * this gate a lapsed org would keep credit-sized headroom and metering would
 * re-push it every tick.
 */
export function desiredGroupCeiling(state: CycleState, now: Date): number {
  const credits = isEntitled(state, now) ? Math.max(0, state.creditBytes) : 0
  return Math.max(
    1,
    state.groupBaselineBytes + state.cycleAllowanceBytes + credits,
  )
}

/** Bytes used beyond BOTH buckets (written off, never billed). Derivable any
 *  time; persisted as one `overage` ledger row only at cycle close. */
export function cycleOverageBytes(
  allowance: number,
  credits: number,
  groupUsed: number,
  groupBaseline: number,
): number {
  return splitCycleUsage(allowance, credits, groupUsed, groupBaseline).overage
}

/** Monotonic cache update: the cached group counter only ever rises, so a stale
 *  or lagging read (or a genuine backend regression) can never lower
 *  `usedThisCycle` and wrongly refund/re-grant. A real regression surfaces as a
 *  small, bounded, customer-favorable under-count — the backend's own posture. */
export function monotonicGroupUsed(cached: number, live: number): number {
  return Math.max(cached, live)
}

/**
 * Renewal grace window. A gateway finalizes and charges a renewal invoice
 * slightly AFTER the period boundary (Stripe: typically ~1h), so an org is
 * still entitled for a short window past `planPeriodEnd` — otherwise a
 * successful renewal would "blink" the org off between the boundary and the
 * payment webhook. The SAME window is applied by the metering lapsed
 * sweep (it only expires/suspends once `now >= planPeriodEnd + grace`), so
 * `isEntitled` and the sweep agree on when a plan has truly lapsed. A genuine
 * dunning failure is therefore cut off ~24h late rather than instantly.
 */
export const RENEWAL_GRACE_MS = 24 * 60 * 60 * 1000

/** Renewal reminder schedule for prepaid terms: a 7-day warning and a
 *  final-day notice. Days, not stages: the value IS the days-left figure the
 *  email states. */
export const RENEWAL_REMINDER_DAYS = [7, 0] as const
export type RenewalReminderStage = (typeof RENEWAL_REMINDER_DAYS)[number]

/**
 * Which renewal reminder (if any) a prepaid term is due for. The 0-day notice
 * fires inside the final 24 hours before `termEndAt`; the 7-day warning fires
 * inside the last 7 days. A term already past its end gets nothing (the
 * renewer lapses it; a reminder for a date in the past is noise). The sent
 * stamps make each stage fire at most once per term end; extending the term
 * clears them, which is both stop-on-payment and re-arming for the next end.
 */
export function renewalReminderStage(
  termEndAt: Date,
  now: Date,
  reminder7SentAt: Date | null,
  reminder0SentAt: Date | null,
): RenewalReminderStage | null {
  const msLeft = termEndAt.getTime() - now.getTime()
  if (msLeft <= 0) return null
  const DAY_MS = 24 * 60 * 60 * 1000
  if (msLeft <= DAY_MS && !reminder0SentAt) return 0
  if (msLeft <= 7 * DAY_MS && !reminder7SentAt && !reminder0SentAt) return 7
  return null
}

/**
 * Whether an org may use the network right now. Access is gated on holding a
 * subscription that is PAID THROUGH the current moment (plus the renewal grace
 * window): `subscriptionRef` set AND `now < planPeriodEnd + grace`. A cancelled
 * subscription clears both; a failed renewal (dunning) has metering null
 * `planPeriodEnd` at grace end — either way this returns false and the org's
 * credentials are suspended, regardless of any remaining data balance. In the
 * single-bucket model plan grants and top-ups share one balance that resets each
 * cycle; the subscription, not the balance, is the access key.
 *
 * Deliberately blind to WHICH rail funds the plan: a comp, an invoiced term and
 * a card subscription all entitle identically, and only the ref's presence
 * matters. That is what lets a new gateway inherit entitlement for free.
 *
 * Pure and isomorphic so both the metering gate (server) and the dashboard
 * (via a server-computed boolean) share one definition of "entitled".
 */
export function isEntitled(
  wallet: {
    subscriptionRef: string | null
    planPeriodEnd: Date | null
  },
  now: Date,
): boolean {
  return (
    wallet.subscriptionRef != null &&
    wallet.planPeriodEnd != null &&
    wallet.planPeriodEnd.getTime() + RENEWAL_GRACE_MS > now.getTime()
  )
}

/** LEGACY stable ref for pre-collapse admin comps. Never written anymore;
 *  kept so `prepaidTermRef` can recognize and preserve it on existing orgs. */
export function compSubscriptionRef(organizationId: string): string {
  return `admin-comp:${organizationId}`
}

/** LEGACY stable ref for pre-invoice wire deals. Never written anymore; kept
 *  so `prepaidTermRef` can recognize and preserve it on existing orgs. */
export function offlineSubscriptionRef(organizationId: string): string {
  return `offline:${organizationId}`
}

/** Stable `subscriptionRef` for a NEW prepaid term, any rail — the rail lives
 *  in its own column now, so the ref encodes nothing (§1.4). In-house handle
 *  that exists purely to satisfy `isEntitled`; never parsed. */
export function termSubscriptionRef(organizationId: string): string {
  return `term:${organizationId}`
}

/**
 * The ref a prepaid grant should use for this org: the org's EXISTING in-house
 * ref when it already carries one (switching refs mid-stream would make every
 * renewal look like a superseded subscription — cycle re-anchor + planExpiry
 * noise on each grant), else the modern `term:{orgId}`. Exact-equality checks
 * against the three known constructions for THIS org — not ref parsing.
 */
export function prepaidTermRef(
  organizationId: string,
  existingRef: string | null,
): string {
  const ours = [
    termSubscriptionRef(organizationId),
    offlineSubscriptionRef(organizationId),
    compSubscriptionRef(organizationId),
  ]
  return existingRef && ours.includes(existingRef)
    ? existingRef
    : termSubscriptionRef(organizationId)
}

/**
 * Whether a plan's price can actually be charged. `microUsdToCents` throws on a
 * fractional cent and `planPerGbMicroUsd` ROUNDS, so a negotiated pair whose
 * implied per-GB rate isn't a whole number of cents produces a plan that renders
 * fine but fails at checkout. Enforced when a custom plan is CREATED so the
 * failure lands on the operator, not the customer.
 */
export function isWholeCentPlan(
  plan: Pick<PricingPlan, 'includedGb' | 'priceMonthlyMicroUsd'>,
): boolean {
  if (plan.includedGb <= 0) return false
  if (plan.priceMonthlyMicroUsd % 10_000 !== 0) return false
  // Exact, not rounded: an inexact division means planPerGbMicroUsd is lying.
  if (plan.priceMonthlyMicroUsd % plan.includedGb !== 0) return false
  return planPerGbMicroUsd(plan) % 10_000 === 0
}

/**
 * What to call the org's plan in the UI. A custom/negotiated plan is private to
 * one org and a retired slug no longer appears in the active catalog, so a null
 * name is expected rather than an error — "Custom plan" is true in both cases.
 * One definition so the dashboard and the billing page cannot disagree (they
 * previously rendered a bare dash and "Your plan" respectively).
 */
export function planDisplayName(planName: string | null): string {
  return planName ?? 'Custom plan'
}

/**
 * True when this tick advanced usage AND left the org's remaining allowance
 * below the alert threshold — the condition for a low-remaining alert email.
 * Deliberately NOT a strict before/after crossing: an org already below the
 * threshold when the feature is enabled (or whose crossing fell between metering
 * ticks) still alerts on its next usage-advancing tick. Deduplication is the
 * caller's job via `organization_billing.lowBalanceNotifiedAt` — without that
 * flag this fires on every advancing tick. Threshold <= 0 disables alerts.
 */
export function shouldLowBalanceAlert(
  usedDelta: number,
  remainingAfter: number,
  threshold: number,
): boolean {
  return threshold > 0 && usedDelta > 0 && remainingAfter < threshold
}

/**
 * Platform default low-balance alert threshold, as a percent of the plan's
 * included data, used when an org hasn't set its own (`lowBalanceAlertPercent`
 * is NULL). A fixed byte amount is meaningless across plan sizes — 1 GB is
 * "already empty" on a 400 GB plan and over-eager on a 5 GB one — so the
 * threshold scales with the plan.
 */
export const LOW_BALANCE_ALERT_PERCENT_DEFAULT = 10

/**
 * Floor for a percent-derived threshold so a small plan still warns with usable
 * runway (10% of 5 GB is 0.5 GB; without a floor a 1 GB plan would warn at
 * 0.1 GB, essentially empty). Only applied when alerts are on and a base is
 * resolvable — it never turns a disabled/basesless org's alerts back on.
 */
export const LOW_BALANCE_FLOOR_BYTES = 0.5 * BYTES_PER_GB

/**
 * The low-balance alert threshold in BYTES for one org. `percent` is the org's
 * `lowBalanceAlertPercent`: NULL → the platform default; 0 (or negative) →
 * alerts OFF; 1..100 → that percent. `baseBytes` is the plan's included data in
 * bytes (the caller resolves it, falling back to the cycle allowance for a
 * plan with no catalog `includedGb`); 0 means no resolvable base, so no alert.
 * Returns 0 whenever alerts should not fire, which `shouldLowBalanceAlert`
 * already treats as disabled.
 */
export function lowBalanceThresholdBytes(
  percent: number | null,
  baseBytes: number,
): number {
  const pct = percent == null ? LOW_BALANCE_ALERT_PERCENT_DEFAULT : percent
  if (pct <= 0 || baseBytes <= 0) return 0
  return Math.max(LOW_BALANCE_FLOOR_BYTES, Math.round((pct / 100) * baseBytes))
}

/**
 * A rough "days of data left at your recent pace" for the low-balance email,
 * from CYCLE-AVERAGE burn: `remaining ÷ (usedThisCycle ÷ days elapsed this
 * cycle)`. Uses only the billing row (no usage-series query), so it's cheap to
 * compute when an alert fires. Returns null when there isn't enough signal to
 * be honest — less than a day into the cycle, no usage yet, or nothing left —
 * and the email omits the line rather than printing "0 days" or "Infinity".
 */
export function estimateDaysLeft(
  remaining: number,
  usedThisCycle: number,
  cycleStartAt: Date | null,
  now: Date,
): number | null {
  if (!cycleStartAt || remaining <= 0 || usedThisCycle <= 0) return null
  const daysElapsed =
    (now.getTime() - cycleStartAt.getTime()) / (24 * 60 * 60 * 1000)
  if (daysElapsed < 1) return null
  const perDay = usedThisCycle / daysElapsed
  if (perDay <= 0) return null
  return Math.max(1, Math.floor(remaining / perDay))
}

/** Whole-GB figure for copy/paperwork: 20480 GB reads as "20 TB", smaller
 *  plans as plain GB. For byte balances use `formatGb`. */
export function formatDataAmount(includedGb: number): string {
  if (includedGb >= 1024 && includedGb % 1024 === 0) {
    return `${includedGb / 1024} TB`
  }
  return `${includedGb} GB`
}

/** Format a byte balance as GB, e.g. 1_500_000_000 → "1.50 GB". */
export function formatGb(bytes: number): string {
  return `${(bytes / BYTES_PER_GB).toFixed(2)} GB`
}

/** Format micro-USD as a USD string, e.g. 1_500_000 → "$1.50" (for amounts paid). */
export function formatMicroUsd(micros: number): string {
  return (micros / MICRO_USD_PER_USD).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  })
}

/**
 * Same amount with the currency code spelled out, e.g. "$1.50 USD".
 *
 * Every price a CUSTOMER is quoted uses this rather than `formatMicroUsd`: the
 * card networks require the transaction currency to be unambiguous at the point
 * of sale, and "$" alone is shared by a dozen currencies. Internal surfaces
 * (admin tables, ledger exports, chart tooltips) keep the bare form, where the
 * currency is already established and the repetition would only cost width.
 */
export function formatMicroUsdWithCurrency(micros: number): string {
  return `${formatMicroUsd(micros)} USD`
}

/**
 * The two axes (BILLING_MODEL_DESIGN.md §1):
 * `BillingMode` is HOW entitlement renews, `PaymentRail` is WHO took the money
 * for the plan. Derived from the Postgres enums like `FundingSource` above.
 */
export type BillingMode = (typeof billingModeEnum.enumValues)[number]
export type PaymentRail = (typeof paymentRailEnum.enumValues)[number]
/**
 * A rail that actually took money. `'none'` means "no plan", which is never a
 * valid thing to stamp on a grant or a term, so the writers take this instead
 * of the full union — and instead of the `'stripe' | 'direct'` literal they
 * used to hardcode, which needed a cast at every call site the moment a third
 * rail existed.
 */
export type FundedRail = Exclude<PaymentRail, 'none'>

/**
 * Whether an org's existing billing state should REFUSE a new plan purchase.
 *
 * Pure and exported so the matrix is testable: it is small but every cell is a
 * money decision, and two of them are counter-intuitive.
 *
 *  - A LAPSED PREPAID term does NOT block. Nothing clears `subscriptionRef` on
 *    that rail — the renewer stops granting once the term ends and metering
 *    expires the bucket without detaching — so blocking on the ref alone would
 *    lock a crypto or invoiced customer out of ever buying again, with no
 *    portal to renew from.
 *  - A lapsed STRIPE subscription still blocks. A past `planPeriodEnd` there
 *    usually means dunning, and the answer is the portal, not a second
 *    subscription.
 *  - ACCOUNT-MANAGED always blocks, in any state. That flag is set by an admin
 *    and cleared by an admin; it is not an entitlement question.
 */
export function blocksNewPlanPurchase(
  billing: {
    subscriptionRef: string | null
    billingMode: BillingMode
    accountManaged: boolean
    planPeriodEnd: Date | null
    /** Display status; only 'trialing' is consulted here. */
    subscriptionStatus?: string | null
  },
  now: Date,
): boolean {
  // Account-managed refuses self-serve UNCONDITIONALLY — live, lapsed, or
  // never billed. It is an explicit admin switch, so a custom plan created
  // mid-negotiation cannot be card-subscribed out from under the deal, and a
  // churned enterprise org returns to self-serve by an admin clearing the flag
  // rather than through an implicit lapse loophole.
  if (billing.accountManaged) return true
  if (!billing.subscriptionRef) return false
  // A free trial never blocks buying a real plan, on any rail: the new grant
  // supersedes the trial (a Stripe trial subscription is cancelled at the
  // gateway; a no-card trial grant is simply replaced).
  if (billing.subscriptionStatus === 'trialing') return false
  // A LAPSED PREPAID term is not a live plan. Nothing clears `subscriptionRef`
  // on that rail — the renewer stops granting at term end and metering expires
  // the bucket without detaching — so blocking on the ref alone locks a crypto
  // customer out of ever buying again, with no portal to renew from.
  const live = isEntitled(
    {
      subscriptionRef: billing.subscriptionRef,
      planPeriodEnd: billing.planPeriodEnd,
    },
    now,
  )
  if (billing.billingMode === 'prepaid' && !live) return false
  return true
}

/** The axis pair for a plan that is actually funded. The grant writer takes
 *  this, so "granted a cycle with no mode or rail" is unrepresentable; the
 *  organization_billing CHECK constraint enforces the same pairs in the DB. */
export interface FundedAxes {
  billingMode: Exclude<BillingMode, 'none'>
  paymentRail: Exclude<PaymentRail, 'none'>
}

/**
 * THE gateway guard (BILLING_MODEL_DESIGN.md §1.3). True only when a payment
 * gateway owns a LIVE auto-renewing subscription for this org — the sole state
 * in which asking a gateway API about the org's subscription ref is
 * meaningful. A prepaid org on the stripe rail has NO subscription object, so
 * asking "which rail?" alone would send reconcile hunting for a ref Stripe has
 * never heard of and ghost-detach a paying customer.
 */
export function gatewayOwnsSubscription(row: {
  billingMode: BillingMode
  paymentRail: PaymentRail
}): boolean {
  // The CHECK constraint already forces subscription -> stripe; asking
  // isGatewayId keeps this exact if that pairing ever widens ('direct' is a
  // rail but never a gateway).
  return row.billingMode === 'subscription' && isGatewayId(row.paymentRail)
}

/**
 * The `ledger_entries.provider` a money/grant row records, derived from the
 * rail. Getting it wrong is silent (the `planGrant` row once hardcoded
 * 'stripe', filing every comp under Stripe in the money history), so it lives
 * here as the single mapping. Historical rows keep their old strings
 * ('admin' for pre-collapse comps, 'offline' for pre-invoice wire deals) —
 * revenue queries spanning the cutover must accept both eras.
 */
export function ledgerProviderFor(rail: Exclude<PaymentRail, 'none'>): string {
  return rail
}

/** The customer-facing label for how an org pays. One definition so the admin
 *  badge and the customer's own billing page cannot disagree. "Comped" is a
 *  DERIVED state (a prepaid term with no paid invoices), decided by callers
 *  that have the money facts — not by the axes alone. */
export function billingLabel(row: {
  billingMode: BillingMode
  paymentRail: PaymentRail
}): string {
  if (row.billingMode === 'subscription') return 'Card'
  if (row.billingMode === 'prepaid') return 'Prepaid'
  return 'None'
}

/**
 * Marker prefix for SEEDED placeholder gateway customer ids, written only by
 * scripts/seed-billing-states.ts so the UI's "has a customer" branches render
 * on seed data. No gateway has ever heard of these: real Stripe ids are `cus_`
 * plus a single base-62 token (never a second underscore), so the marker
 * cannot collide with one. Any code that would call a gateway API about a
 * customer (the reconcile backstop, admin diagnostics) must skip them — the
 * call is doomed by construction, and a staging environment re-seeds these
 * daily. A customer id created through a real gateway (even test mode) never
 * matches, so those still reconcile normally.
 */
export const SEEDED_CUSTOMER_ID_PREFIX = 'cus_seed_'

export function isSeededCustomerId(id: string): boolean {
  return id.startsWith(SEEDED_CUSTOMER_ID_PREFIX)
}
